import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { isFinaPagoIntroDismissed } from '@common/constants/fina-pago-storage';
import { FinaPaymentsSettings } from '@common/models';
import { FinaPaymentsService } from '@common/services';
import { UserService } from '@modules/auth/services';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export type FinaPagoPillVariant = 'activate' | 'verify' | null;

// Píldora de Fina Pago en la barra superior (handoff, bloque `showMini/showPending`):
//  - «Activar Fina Pago»  (violeta claro) tras «Ahora no» en la intro, mientras no haya activación
//  - «Verificar Fina Pago» (ámbar) con una activación en revisión o rechazada
//  - desaparece cuando Fina Pago está activo
// Abre el modal de activación a través de su servicio (import dinámico: bank-accounts es lazy y
// ya depende del módulo de navegación, así que no puede importarse estáticamente desde aquí).
@Component({
    selector: 'fina-fina-pago-topnav-pill',
    templateUrl: './fina-pago-topnav-pill.component.html',
    styleUrls: ['./fina-pago-topnav-pill.component.scss'],
})
export class FinaPagoTopnavPillComponent implements OnInit, OnDestroy {
    flagOn = false;
    settings: FinaPaymentsSettings | null = null;
    loaded = false;
    opening = false;

    private subs: Subscription[] = [];

    constructor(private finaPaymentsService: FinaPaymentsService, private userService: UserService, private injector: Injector) {}

    ngOnInit(): void {
        this.subs.push(this.finaPaymentsService.settings$.subscribe(settings => (this.settings = settings)));
        // Flag `finapago`: sin él no hay píldora ni promo. Cuando PostHog lo enciende (puede llegar
        // después del login) se cargan los settings y se evalúa la promo en ese momento.
        this.subs.push(
            this.finaPaymentsService.enabled$.subscribe(enabled => {
                this.flagOn = enabled;
                if (enabled) this.loadWhenManager();
            }),
        );
    }

    /**
     * Carga perezosa: solo si el usuario puede activar (owner con permiso de cuentas). Al recargar la
     * página el usuario llega después de que la topbar se crea, así que se espera a que esté.
     */
    private loadWhenManager(): void {
        this.subs.push(
            this.userService.user$
                .pipe(
                    filter(() => this.canManage),
                    take(1),
                )
                .subscribe(() => this.loadSettings()),
        );
    }

    private loadSettings(): void {
        if (!this.flagOn) return;
        this.subs.push(
            this.finaPaymentsService.getSettings$().subscribe({
                next: res => {
                    this.loaded = true;
                    // Hero promocional al entrar a Fina (desde el dashboard), una vez por comercio y
                    // solo mientras no haya activación. Vive aquí porque la topbar está en todo el panel.
                    if (this.flagOn && !res?.settings) this.openPromoOnce();
                },
                error: () => {},
            }),
        );
    }

    private async openPromoOnce(): Promise<void> {
        const tenantId = this.userService?.user$?.getValue?.()?.tenantId;
        if (!tenantId) return;
        const m = await import('@modules/bank-accounts/services/fina-payments-activation.service');
        this.injector.get(m.FinaPaymentsActivationService).openPromoOnce(tenantId);
    }

    ngOnDestroy(): void {
        this.subs.forEach(s => s.unsubscribe());
    }

    get canManage(): boolean {
        const user = this.userService?.user$?.getValue?.() ?? null;
        return !!user?.isOwner && (user?.permissions?.BANK_ACCOUNTS?.includes('add-bank-accounts') ?? false);
    }

    get variant(): FinaPagoPillVariant {
        if (!this.flagOn || !this.canManage || !this.loaded) return null;
        // Visible en todas las pantallas (Home incluida) hasta que Fina Pago quede activo por completo:
        // verificación pendiente, en revisión o fallida → «Verificar»; sin activar → «Activar», solo
        // después de «Ahora no» en la promo (antes de eso la promo es la que invita a activar).
        const status = this.settings?.status;
        if (status === 'ACTIVE') return null;
        if (status === 'VERIFYING' || status === 'UNCERTAIN' || status === 'FAILED') return 'verify';
        return isFinaPagoIntroDismissed() ? 'activate' : null;
    }

    get label(): string {
        // Con el logo en la cápsula, «Activar» no repite la marca; «Verificar» lleva el reloj y sí la nombra.
        return this.variant === 'verify' ? 'Verificar Fina Pago' : 'Activar';
    }

    async open(): Promise<void> {
        if (this.opening) return;
        this.opening = true;
        try {
            const m = await import('@modules/bank-accounts/services/fina-payments-activation.service');
            const service = this.injector.get(m.FinaPaymentsActivationService);
            if (this.variant === 'verify') {
                service.open({ entryPoint: 'topbar', startAt: this.settings?.status === 'FAILED' ? 'pick' : 'manage' });
            } else {
                service.open({ entryPoint: 'topbar', startAt: 'intro' });
            }
        } finally {
            this.opening = false;
        }
    }
}
